/**
 * Shared state of one new-chart wizard run (W2-14 onboarding-wizard) beside the contract's `NewChartDraft`:
 *  - the draft itself, as a small external store (the dialog renders from it; background work that finishes after
 *    a step was left reads the latest draft and updates it only while it still describes the same song);
 *  - decoded songs and their sniffed codec by SHA-256 (going back to the Song step needs no second decode); a song
 *    converted to WAV is the same decoded song under a second SHA-256, so `holdsSong` recognises it;
 *  - the background tempo detection of the current song (`beginDetection` / `endDetection` / `detectionOf`): it
 *    outlives the Song step (the user may move on while it runs) and survives the WAV conversion;
 *  - the title / artist the wizard filled in itself (a new song may replace them while the user has not typed);
 *  - whether the user edited the preview range (a new song then keeps the range, fitted into the song).
 *
 * Also here: `useStepActions` (stable `update` / `setCanContinue` of a step's props), `useSongPlayer` (plays a
 * decoded song or a range of it on the app's AudioContext with the user's music volume), `useServicesText` and
 * `useSettingsValue`.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import type { AppServices, NewChartDraft, WizardStepProps } from '../../app/registry/types';
import type { Locale, MessageRef, Unsubscribe } from '../../core/util/types';
import type { MediaSniff } from '../../formats/types';
import type { I18n } from '../../i18n/types';
import { wakeAudio, type DecodedSong } from './songAnalysis';
export { useSettings as useSettingsValue } from '../../app/registry/react';

/** Background tempo detection of a song. */
export type DetectionState = 'running' | 'done' | 'failed';

export class WizardSession {
  private current: NewChartDraft;
  private forward: ((p: Partial<NewChartDraft>) => void) | null;
  private readonly listeners = new Set<() => void>();
  private readonly songs = new Map<string, DecodedSong>();
  private readonly sniffs = new Map<string, MediaSniff>();
  private auto = { name: '', composer: '' };
  private rangeEdited = false;
  private detection: { readonly song: DecodedSong; readonly token: number; readonly state: DetectionState } | null =
    null;
  private detectionCount = 0;

  /** `forward`: when given, updates also go to the owner of the draft (a step hosted outside the wizard dialog). */
  constructor(draft: NewChartDraft, forward: ((p: Partial<NewChartDraft>) => void) | null = null) {
    this.current = draft;
    this.forward = forward;
  }

  readonly draft = (): NewChartDraft => this.current;

  readonly update = (patch: Partial<NewChartDraft>): void => {
    this.current = { ...this.current, ...patch };
    this.forward?.(patch);
    this.emit();
  };

  private emit(): void {
    for (const cb of this.listeners) cb();
  }

  readonly subscribe = (cb: () => void): Unsubscribe => {
    this.listeners.add(cb);
    return () => {
      this.listeners.delete(cb);
    };
  };

  /** Follows a draft owned elsewhere (standalone steps). */
  sync(draft: NewChartDraft, forward: (p: Partial<NewChartDraft>) => void): void {
    this.current = draft;
    this.forward = forward;
  }

  song(sha: string): DecodedSong | undefined {
    return this.songs.get(sha);
  }

  hasSong(sha: string): boolean {
    return this.songs.has(sha);
  }

  sniff(sha: string): MediaSniff | undefined {
    return this.sniffs.get(sha);
  }

  putSong(sha: string, song: DecodedSong, sniff: MediaSniff): void {
    this.songs.set(sha, song);
    this.sniffs.set(sha, sniff);
  }

  /** Does the draft hold `song` now (the same decoded song, also after its file was converted to WAV)? */
  holdsSong(song: DecodedSong): boolean {
    const music = this.current.music;
    return music !== null && this.songs.get(music.sha256) === song;
  }

  /** A background detection of `song` starts (it replaces any earlier one); returns its token for `endDetection`. */
  beginDetection(song: DecodedSong): number {
    this.detectionCount++;
    this.detection = { song, token: this.detectionCount, state: 'running' };
    this.emit();
    return this.detectionCount;
  }

  /** Detection `token` ended (ignored when a newer detection started since). */
  endDetection(token: number, state: Exclude<DetectionState, 'running'>): void {
    if (this.detection?.token !== token) return;
    this.detection = { ...this.detection, state };
    this.emit();
  }

  /** State of the latest detection when it is `song`'s, else null. */
  detectionOf(song: DecodedSong | undefined): DetectionState | null {
    return song !== undefined && this.detection?.song === song ? this.detection.state : null;
  }

  get autoName(): string {
    return this.auto.name;
  }

  get autoComposer(): string {
    return this.auto.composer;
  }

  setAutoFilled(name: string | null, composer: string | null): void {
    this.auto = { name: name ?? this.auto.name, composer: composer ?? this.auto.composer };
  }

  get previewEdited(): boolean {
    return this.rangeEdited;
  }

  markPreviewEdited(): void {
    this.rangeEdited = true;
  }
}

const SessionContext = createContext<WizardSession | null>(null);

export const WizardSessionProvider = SessionContext.Provider;

/**
 * The session of the surrounding wizard dialog; a step rendered on its own (tests, other hosts) gets a session of
 * its own that follows its props.
 */
export function useWizardSession(props: Pick<WizardStepProps, 'draft' | 'update'>): WizardSession {
  const shared = useContext(SessionContext);
  const [own] = useState(() => new WizardSession(props.draft));
  useEffect(() => {
    if (shared === null)
      own.sync(props.draft, (p) => {
        props.update(p);
      });
  });
  return shared ?? own;
}

/** Stable `update` / `setCanContinue` callbacks over a step's (possibly changing) props. */
export function useStepActions(props: WizardStepProps): {
  readonly update: (p: Partial<NewChartDraft>) => void;
  readonly setCanContinue: (ok: boolean) => void;
} {
  const latest = useRef(props);
  useEffect(() => {
    latest.current = props;
  });
  const update = useCallback((p: Partial<NewChartDraft>): void => {
    latest.current.update(p);
  }, []);
  const setCanContinue = useCallback((ok: boolean): void => {
    latest.current.setCanContinue(ok);
  }, []);
  return useMemo(() => ({ update, setCanContinue }), [update, setCanContinue]);
}

// ---------------------------------------------------------------------------------------------------------------
// Text and settings
// ---------------------------------------------------------------------------------------------------------------

export interface ServicesText {
  readonly locale: Locale;
  readonly t: I18n['t'];
  readonly tr: (ref: MessageRef) => string;
}

/** Translation through `services.i18n`, re-rendering on locale changes. */
export function useServicesText(services: Pick<AppServices, 'i18n'>): ServicesText {
  const { i18n } = services;
  const subscribe = useCallback((cb: () => void): Unsubscribe => i18n.subscribe(cb), [i18n]);
  const get = useCallback((): Locale => i18n.locale, [i18n]);
  const locale = useSyncExternalStore(subscribe, get, get);
  return useMemo(() => ({ locale, t: (key, params) => i18n.t(key, params), tr: (r) => i18n.tr(r) }), [i18n, locale]);
}

// ---------------------------------------------------------------------------------------------------------------
// Song preview playback
// ---------------------------------------------------------------------------------------------------------------

export interface SongPlayer {
  /** Id of what is playing (callers pass one per play button), or null. */
  readonly playing: string | null;
  /** Song time (s) of the playback now, or null when stopped. */
  readonly position: () => number | null;
  /** Plays `song` from `fromSec` (to `toSec` when given); false when audio is unavailable. */
  readonly play: (id: string, song: DecodedSong, fromSec: number, toSec?: number) => boolean;
  readonly stop: () => void;
}

interface Running {
  readonly source: AudioBufferSourceNode;
  readonly ctx: AudioContext;
  readonly startedAt: number;
  readonly fromSec: number;
  readonly toSec: number;
}

/** Plays decoded songs through the app's AudioContext (music volume applied); stops when the component unmounts. */
export function useSongPlayer(services: Pick<AppServices, 'audio' | 'settings'>): SongPlayer {
  const running = useRef<Running | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);

  const halt = useCallback((): void => {
    const r = running.current;
    running.current = null;
    if (r === null) return;
    r.source.onended = null;
    try {
      r.source.stop();
    } catch {
      // Already stopped.
    }
    r.source.disconnect();
  }, []);

  const stop = useCallback((): void => {
    halt();
    setPlaying(null);
  }, [halt]);

  useEffect(() => halt, [halt]);

  const play = useCallback(
    (id: string, song: DecodedSong, fromSec: number, toSec?: number): boolean => {
      halt();
      wakeAudio(services);
      const ctx = services.audio.context;
      const length = song.channels[0]?.length ?? 0;
      if (ctx === null || length === 0) {
        setPlaying(null);
        return false;
      }
      const buffer = ctx.createBuffer(song.channels.length, length, song.sampleRate);
      song.channels.forEach((c, i) => {
        buffer.copyToChannel(new Float32Array(c), i);
      });
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      const gain = ctx.createGain();
      const v = services.settings.value.audio.volumes;
      gain.gain.value = Math.max(0, Math.min(1, v.master * v.music));
      source.connect(gain);
      gain.connect(ctx.destination);
      const from = Math.max(0, Math.min(song.durationSec, fromSec));
      const to = toSec === undefined ? song.durationSec : Math.max(from, Math.min(song.durationSec, toSec));
      source.onended = () => {
        if (running.current?.source === source) {
          running.current = null;
          setPlaying(null);
        }
      };
      source.start(0, from, to - from);
      running.current = { source, ctx, startedAt: ctx.currentTime, fromSec: from, toSec: to };
      setPlaying(id);
      return true;
    },
    [services, halt],
  );

  const position = useCallback((): number | null => {
    const r = running.current;
    if (r === null) return null;
    return Math.min(r.toSec, r.fromSec + Math.max(0, r.ctx.currentTime - r.startedAt));
  }, []);

  return useMemo(() => ({ playing, position, play, stop }), [playing, position, play, stop]);
}
